import type { ReactNode } from "react";
import { fa } from "../i18n/fa";

/**
 * WHAT HAS HAPPENED TO A SEAT, SAID ON THAT SEAT — the card tables' notice slot (operator,
 * 2026-09-15).
 *
 * The card tables used to announce a drop and a departure with `ReconnectingToast` / `LeftToast` in
 * `TopNoticeStack`, at `safeTop + 58px` — a height chosen for the two-seat board games' 40 px header
 * band. On a card table that is the felt. Measured on real staging tables (`probe-card-pill.cjs`):
 * the pill covered حکم's trick counter, trump pill, hakem chip and partner, بی‌دل's round counter,
 * پاسور's and بلوف's opponents, and on شلم and بلوف the «خروج» button — for the dropped seat's whole
 * 60 s grace, and for five seconds at every takeover. There is no spot off the felt that covers
 * nothing at 320/360, so what happens to a CHAIR is said on that chair: the card tables' equivalent
 * of شطرنج, where the pill sits on the absent opponent's own strip.
 *
 * ⚠️ THE TEXT IS SHORT BECAUSE IT REPLACES A NAME, in labels built for names (حکم 74 px of text,
 * بی‌دل 70 px at 14 px bold, بلوف ~78 px once the count moves onto the avatar). Measured in a real
 * browser at 12 px bold: «در حال اتصال» 64.3 px · «قطع شد» 40.2 · «از میز رفت» 50.0 · «بازی نکرد»
 * 42.5. The forms that did NOT fit and were rejected: «در حال اتصال…» 73.2, «ارتباط قطع شد» 70.2,
 * «چند دور بازی نکرد» 83.1.
 * ⚠️ THE WHOLE SENTENCE SURVIVES AS THE ACCESSIBLE NAME — «ارتباط «X» قطع شد؛ ربات جایگزینش شد» is
 * what a screen reader reads and what a probe finds the seat by; only the painted text is short.
 * ⚠️ `dim` IS THE GRACE, NOT THE DEPARTURE. A seat waiting for its player dims; a seat a bot has
 * taken over is playing again and must not look absent.
 */
export interface SeatNotice {
  readonly seat: number;
  /** The short text the seat's own label paints while this is up. */
  readonly text: string;
  /** The full sentence, naming the player — the accessible label. */
  readonly label: string;
  /** Fade the seat's avatar: its player is away, not replaced. */
  readonly dim: boolean;
}

/** The seat's label while a notice is on it. Its size is fixed here, not inherited: see above. */
export function SeatNoticeText({ notice }: { notice: SeatNotice }): ReactNode {
  return (
    <span
      role="status"
      aria-label={notice.label}
      style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0 }}
    >
      {notice.text}
    </span>
  );
}

/** How far a seat's avatar dims while its player is away — applied to the seat's EXISTING ring
 *  wrapper, never to a new element around `Avatar` (see `Avatar`'s note on ad-hoc wrappers). */
export const SEAT_NOTICE_DIM = 0.45;

/** The notice for `seat`, or null — the one lookup every card table does. */
export function noticeForSeat(
  notices: readonly SeatNotice[] | undefined,
  seat: number,
): SeatNotice | null {
  return notices?.find((n) => n.seat === seat) ?? null;
}

/**
 * The SHORT text for a departure the server has just announced (`left`), by its reason.
 *
 * ⚠️ THE THREE REASONS MUST STAY APART, and the long copy says why: «رفت» is a decision and
 * «قطع شد» is an accident, and an IDLE removal must not say «از میز رفت» at all — they did not
 * leave, and telling the table they did was half of the 2026-07-23 bug.
 */
export function seatLeftText(reason: string): string {
  if (reason === "disconnected") return fa.online.seatDisconnected;
  if (reason === "idle") return fa.online.seatIdle;
  return fa.online.seatLeft;
}
